import {
  IChartApi,
  ISeriesApi,
  LineSeries,
  LineStyle
} from 'lightweight-charts';
import { useCallback, useMemo } from 'react';
import { IndicatorType } from '@shared/types';
import { useChartContext } from '../components/chart.context';
import { useSubplotSeries } from './use-subplot-series';
import {
  isRsiIndicatorUpdate,
  RsiIndicatorUpdate
} from '../services/indicator-update.service';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';

type RsiSeriesGroup = {
  chart: IChartApi;
  rsi: ISeriesApi<'Line'>;
  ma: ISeriesApi<'Line'>;
  overbought: ISeriesApi<'Line'>;
  oversold: ISeriesApi<'Line'>;
};

const OVERBOUGHT_LEVEL = 70;
const OVERSOLD_LEVEL = 30;

const toLineData = (
  points: RsiIndicatorUpdate['data'],
  valueSelector: (
    point: RsiIndicatorUpdate['data'][number]
  ) => number | undefined
) =>
  // Preserve timestamps with whitespace points so subplot keeps the same logical timeline.
  points.map((point) => {
    const value = valueSelector(point);
    return typeof value === 'number'
      ? { time: point.time, value }
      : { time: point.time };
  });

export const useRsiSeries = () => {
  const {
    chartInitialized,
    indicatorSeriesRefs,
    data: { indicatorConfigs }
  } = useChartContext();

  const { initializeChartAndSeriesArray } = useSubplotSeries();

  const hasRsi = useMemo(
    () =>
      indicatorConfigs.subplot.some(
        (indicator) => indicator.type === IndicatorType.RSI
      ),
    [indicatorConfigs.subplot]
  );

  const isLastIndicator = useMemo(
    () => indicatorConfigs.subplot.at(-1)?.type === IndicatorType.RSI,
    [indicatorConfigs.subplot]
  );

  const getOrCreateSeriesGroup = useCallback(
    (indicatorIndex: number): RsiSeriesGroup | null => {
      const existing = indicatorSeriesRefs.current[IndicatorType.RSI]?.[
        indicatorIndex
      ] as RsiSeriesGroup | undefined;
      if (existing) return existing;

      const { chart, seriesArray } = initializeChartAndSeriesArray(
        IndicatorType.RSI,
        isLastIndicator
      );

      const rsi = chart.addSeries(LineSeries, {
        color: 'blue',
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false
      });

      const ma = chart.addSeries(LineSeries, {
        color: 'yellow',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false
      });

      const overbought = chart.addSeries(LineSeries, {
        color: 'grey',
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        priceLineVisible: false,
        lastValueVisible: false
      });

      const oversold = chart.addSeries(LineSeries, {
        color: 'grey',
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        priceLineVisible: false,
        lastValueVisible: false
      });

      const group: RsiSeriesGroup = {
        chart,
        rsi,
        ma,
        overbought,
        oversold
      };

      seriesArray[indicatorIndex] = group;
      return group;
    },
    [initializeChartAndSeriesArray, isLastIndicator]
  );

  const applyUpdate = useCallback(
    (update: RsiIndicatorUpdate) => {
      const seriesGroup = getOrCreateSeriesGroup(update.indicatorIndex);
      if (!seriesGroup) return;

      if (update.updateType === 'snapshot') {
        // Snapshot replaces full RSI/MA state for this indicator index.
        seriesGroup.rsi.setData(toLineData(update.data, (p) => p.value));
        seriesGroup.ma.setData(toLineData(update.data, (p) => p.ma));

        const times = update.data.map((p) => p.time);
        seriesGroup.overbought.setData(
          times.map((time) => ({ time, value: OVERBOUGHT_LEVEL }))
        );
        seriesGroup.oversold.setData(
          times.map((time) => ({ time, value: OVERSOLD_LEVEL }))
        );

        seriesGroup.chart.timeScale().fitContent();
        return;
      }

      update.data.forEach((point) => {
        // Delta points update current bar, append a new bar, or mark whitespace when value is missing.
        seriesGroup.rsi.update(
          typeof point.value === 'number'
            ? { time: point.time, value: point.value }
            : { time: point.time }
        );
        seriesGroup.ma.update(
          typeof point.ma === 'number'
            ? { time: point.time, value: point.ma }
            : { time: point.time }
        );

        seriesGroup.overbought.update({
          time: point.time,
          value: OVERBOUGHT_LEVEL
        });
        seriesGroup.oversold.update({
          time: point.time,
          value: OVERSOLD_LEVEL
        });
      });
    },
    [getOrCreateSeriesGroup]
  );

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasRsi,
    indicatorType: IndicatorType.RSI,
    isExpectedUpdate: isRsiIndicatorUpdate,
    applyUpdate
  });
};
